'use client'

import type { FormStore, FormStoreProps } from '@ariakit/react'
import type { ZodType } from 'zod'

type FormStoreValues = NonNullable<FormStoreProps['defaultValues']>

/**
 * Ariakit's form-level error key. Zod issues with an empty `path` come from schema-level checks
 * that don't name a field, so they have no input to attach to and are surfaced as a form-level error instead.
 */
const FORM_ERROR_NAME = '_form'

/**
 * Parse `values` with `schema` and mirror any issues onto the form store as field errors.
 *
 * Zod reports every issue it finds, while Ariakit renders a single message per field, so only the
 * first issue for a given path is kept. Paths are joined with `.` to match Ariakit's field naming
 * (e.g. an issue at `['emailDomains', 0]` becomes `emailDomains.0`).
 */
export function setZodFormErrors<T extends FormStoreValues>(
  form: FormStore<T>,
  schema: ZodType,
  values: unknown
): boolean {
  const result = schema.safeParse(values)
  if (result.success) return true

  const handledPaths = new Set<string>()
  for (const issue of result.error.issues) {
    const name = issue.path.length > 0 ? issue.path.join('.') : FORM_ERROR_NAME
    if (handledPaths.has(name)) continue
    handledPaths.add(name)
    form.setError(name, issue.message)
  }

  return false
}

export function useZodFormValidate<T extends FormStoreValues>(
  form: FormStore<T>,
  schema: ZodType | undefined
): void {
  form.useValidate(state => {
    if (!schema) return
    form.setErrors({})
    setZodFormErrors(form, schema, state.values)
  })
}
